import { createSelector } from "reselect";
import compact from "lodash/compact";
import { getPropertyValue } from "utils/misc/css-util";

const emptyAccountDetail = {
  sumBalAmt: null,
  currCd: null,
  accountList: [],
};

export const PortfolioSelector = {
  state: (state) => state.ultrahigh.portfolio,
  isRmUser: (state) => PortfolioSelector.state(state).isRmUser,
  fetchingSavingDeposit: (state) => PortfolioSelector.state(state).fetchingSavingDeposit,
  fetchingStructureNote: (state) => PortfolioSelector.state(state).fetchingStructureNote,
  fetchingCustomerCareContact: (state) => PortfolioSelector.state(state).fetchingCustomerCareContact,
  customerCareContact: (state) => PortfolioSelector.state(state).customerCareContact,

  bankDeposit: (state) => PortfolioSelector.state(state).bankDeposit ?? {},
  totalBankDepositAmount: (state) => PortfolioSelector.bankDeposit(state).totalAmt ?? 0,
  percentage: (state) => PortfolioSelector.bankDeposit(state).percentage ?? {},
  savingDepositAccount: (state) => PortfolioSelector.bankDeposit(state).savingDepositAccount ?? emptyAccountDetail,
  currentDepositAccount: (state) => PortfolioSelector.bankDeposit(state).currentDepositAccount ?? emptyAccountDetail,
  fixedDepositAccount: (state) => PortfolioSelector.bankDeposit(state).fixedDepositAccount ?? emptyAccountDetail,
  foreignCurrencyDepositAccountList: (state) =>
    PortfolioSelector.bankDeposit(state).foreignCurrencyDepositAccountList ?? emptyAccountDetail,

  structureNote: (state) => PortfolioSelector.state(state).structureNote ?? {},
  totalStructureNoteAmount: (state) => PortfolioSelector.structureNote(state).totalAmt ?? 0,
  structureNoteAccount: (state) => PortfolioSelector.structureNote(state).accountList ?? [],

  fetchingBond: (state) => PortfolioSelector.state(state).fetchingBond,
  totalBondWealth: (state) => PortfolioSelector.state(state).bond?.totalWealth ?? 0,
  bondCategories: (state) => PortfolioSelector.state(state).bond?.bondCategories ?? [],

  fetchingMutualFund: (state) => PortfolioSelector.state(state).fetchingMutualFund,
  mutualFund: (state) => PortfolioSelector.state(state).mutualFund,
  mutualFundTotalAmtSummary: (state) => PortfolioSelector.mutualFund(state).summary?.totalAmt ?? 0,
  mutualFundAsset: (state) => PortfolioSelector.mutualFund(state).assets,
  fetchingUnitHolder: (state) => PortfolioSelector.mutualFund(state).fetchingUnitHolder,
  unitholders: (state) => PortfolioSelector.mutualFund(state).unitholders,
  filterUnitholder: (state) => PortfolioSelector.mutualFund(state).filterUnitholder,
  fundTypeSelected: (state) => PortfolioSelector.mutualFund(state).fundTypeSelected,
  currencyTypeSelected: (state) => PortfolioSelector.mutualFund(state).currencyTypeSelected,
  mutualFundCriteries: (state) => PortfolioSelector.mutualFundAsset(state).criteries,
  fetchingMutualFundByType: (state) => PortfolioSelector.mutualFund(state).fetchingMutualFundByType,
  mutualFundSpecificType: (state) => PortfolioSelector.mutualFund(state).mutualFundSpecificType,
  fundSelected: (state) => PortfolioSelector.mutualFund(state).fundSelected,
  fundTransaction: (state) => PortfolioSelector.mutualFund(state).transaction,
  filterTransactionType: (state) => PortfolioSelector.mutualFund(state).filterTransactionType,

  mutualFundAssetType: createSelector(
    [(state) => PortfolioSelector.mutualFundAsset(state).mutualFundTypeList],
    (mutualFundTypeList) => {
      if (!mutualFundTypeList || mutualFundTypeList.length === 0) return [];
      return mutualFundTypeList.map((mutualFund) => {
        return {
          ...mutualFund,
          color: getPropertyValue({ varName: `--chart-color-${+mutualFund.mfTypeCode}` }),
          index: +mutualFund.mfTypeCode,
        };
      });
    }
  ),

  mutualFundAssetChart: createSelector(
    [(state) => PortfolioSelector.mutualFundAssetType(state)],
    (mutualFundAssetType) => {
      if (!mutualFundAssetType.length) return [];
      const isAllZeroAmount = mutualFundAssetType.every((data) => (data.mfTypeCurrentAmt ?? 0) === 0);
      return compact(
        mutualFundAssetType.map((data) => {
          if (!isAllZeroAmount && !data.mfTypeCurrentAmt) return null;
          return {
            label: data.mfTypeName,
            value: data.mfTypeCurrentAmt ?? 0,
            percentage: data.mfTypePercentage ?? 0,
            backgroundColor: data.color,
            borderColor: data.color,
            className: `text-white chart-${data.index}`,
          };
        })
      );
    }
  ),

  assetOverAllChart: createSelector(
    [
      (state) => PortfolioSelector.totalBankDepositAmount(state),
      (state) => PortfolioSelector.mutualFundTotalAmtSummary(state),
      (state) => PortfolioSelector.totalBondWealth(state),
      (state) => PortfolioSelector.totalStructureNoteAmount(state),
    ],
    (totalBankDepositAmount, mutualFundTotalAmtSummary, totalBondWealth, totalStructureNoteAmount) => {
      return compact([
        !!totalBankDepositAmount && {
          label: "เงินฝากธนาคาร",
          value: totalBankDepositAmount,
          backgroundColor: "#0B5991",
          borderColor: "#0B5991",
          className: "deposit",
        },
        !!mutualFundTotalAmtSummary && {
          label: "กองทุนรวม",
          value: mutualFundTotalAmtSummary,
          backgroundColor: "#0077CC",
          borderColor: "#0077CC",
          className: "mutual-fund",
        },
        !!totalBondWealth && {
          label: "ตราสารหนี้",
          value: totalBondWealth,
          backgroundColor: "#0A99FF",
          borderColor: "#0A99FF",
          className: "bond",
        },
        !!totalStructureNoteAmount && {
          label: "หุ้นกู้ที่มีอนุพันธ์แฝง",
          value: totalStructureNoteAmount,
          backgroundColor: "#47B3FF",
          borderColor: "#47B3FF",
          className: "complex",
        },
      ]);
    }
  ),

  bankDepositAssetType: createSelector([(state) => PortfolioSelector.percentage(state)], (bankDeposit) => {
    if (!bankDeposit) return [];

    const colors = {
      บัญชีเงินฝากออมทรัพย์: "#FF8F01",
      บัญชีเงินฝากกระแสรายวัน: "#FCCC00",
      บัญชีเงินฝากประจำ: "#41DB5B",
      บัญชีเงินฝากเงินตราต่างประเทศ: "#58C5F5",
    };
    return bankDeposit.map((data, index) => {
      if (!data) return null;
      return {
        ...data,
        color: colors[data?.label],
        index: index + 1,
      };
    });
  }),

  bankDepositAssetChart: createSelector(
    [(state) => PortfolioSelector.bankDepositAssetType(state)],
    (bankDepositAssetType) => {
      return compact(
        bankDepositAssetType.map((data) => {
          if (!data.value) return null;
          return {
            label: data.label,
            value: data.value,
            percentage: data.percentage,
            backgroundColor: data.color,
            borderColor: data.color,
            className: `text-white chart-${data.index}`,
          };
        })
      );
    }
  ),

  bondAssetType: createSelector([(state) => PortfolioSelector.bondCategories(state)], (bondCategories) => {
    if (!bondCategories) return [];
    const colors = {
      "01": "#FF8F01",
      "02": "#FCCC00",
      "03": "#41DB5B",
    };

    return bondCategories.map((bond, index) => {
      if (!bond) return null;
      return {
        categoryName: bond?.categoryName,
        percentage: bond?.percentage,
        value: bond?.totalWealth,
        color: colors[bond?.categoryCode],
        index: index + 1,
      };
    });
  }),

  bondAssetChart: createSelector([(state) => PortfolioSelector.bondAssetType(state)], (bondAssetType) => {
    return compact(
      bondAssetType.map((data) => {
        if (!data.value) return null;
        return {
          label: data.categoryName,
          value: data.value,
          percentage: data.percentage,
          backgroundColor: data.color,
          borderColor: data.color,
          className: `text-white chart-${data.index}`,
        };
      })
    );
  }),
};
